//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Card from 'react-credit-cards';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';
import _some from 'lodash/some';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { ADD_REMOVE_ADDITIONAL_SEATS, CHANGE_BILLING_INTERVAL, PURCHASE, PURCHASE_ADDITIONAL_SEATS } from '../../common/constants';
import { PLACEHOLDER_FIELD_NAME } from '../../common/placeholderHelper';
import { getTierTitle } from '../../common/lovHelper';
import {
    getFormattedDate,
    //  getFormattedTime,
    getUserDateFormat,
} from '../../common/dateHelper';
import {
    createCardSetupIntent,
    // createCardSetupIntent,
    createStripeAdditionalSeatSubscription,
    createStripeSubscription,
    retrieveStripeCustomer,
    upgradeStripeAdditionalSeatSubscription,
    upgradeStripeSubscription,
} from '../../common/services/paymentServices';
import { numberWithCommas, getFormattedPrice } from '../../common/textHelper';
import { getConfirmCardPaymentBillingDetails, getConfirmCardSetupBillingDetails } from '../../common/paymentHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import {
    activateSubscription,
    /* validatePromoCode, */ activateAdditionalSeats,
    upgradeTierSubscription,
    activateTier1Subscription,
    saveEmployerSetupIntentCardPaymentMethod,
} from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdNumberFormat from '../../common/elemental/AfitdNumberFormat';
// import AfitdDatePicker from '../../common/elemental/AfitdDatePicker';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    // const { } = params;

    const errorMessageStyles = { color: 'red' };

    return makeStyles(theme => ({
        inputBoxContainer: { display: 'flex' },
        inputBox: { flex: 1, padding: '0 5px', marginTop: 10 },
        inline: { display: 'flex', alignItems: 'center' },

        title: { fontWeight: 'bold', margin: '10px 0' },
        existingCardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        cardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        label: { margin: '10px 0 5px 0', fontWeight: 'bold' },

        cardPrice: { fontWeight: 'bold', fontSize: 21 },

        payingContainer: { marginTop: 20, paddingTop: 20, borderTop: '2px solid rgb(240,242,246)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        payingDetailsContainer: { display: 'flex', flexDirection: 'column' },
        payingDetailsLineTwo: { fontWeight: 'bold' },
        payingDetailsLineOne: { fontWeight: 'bold' },
        payingDetailsPayNowText: { fontWeight: 'bold', marginRight: 20 },
        circularContainer: {
            padding: '20px',
            textAlign: 'center',
        },

        stripeCardNumberErrorMessage: errorMessageStyles,
        stripeCardExpiryErrorMessage: errorMessageStyles,
        stripeCardCvcErrorMessage: errorMessageStyles,
        lineItemDiv: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: '8px',
            paddingTop: '8px',
        },
        lineItemDescription: {
            position: 'relative',
            paddingLeft: '20px',
        },
        dot: {
            fontSize: '1.5rem',
            top: '3px',
            left: '0px',
            position: 'absolute',
        },
        stripeCardElementDiv: {
            border: '2px solid lightgrey',
            padding: '15px',
            borderRadius: '5px',
        },
    }));
};

//---------------------------------------------------------------------
// Helper function
//---------------------------------------------------------------------
// // will format the expiry date
// function limit(val, max) {
//     if (val.length === 1 && val[0] > max[0]) val = '0' + val;

//     if (val.length === 2) {
//         if (Number(val) === 0) val = '01';
//         else if (val > max) val = max;
//     }
//     return val;
// }

// function checkCardExpiryDate(val) {
//     const month = limit(val.substring(0, 2), '12');
//     const year = val.substring(2, 4);

//     return month + (year.length ? '/' + year : '');
// }

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        stripeAdditionalSeatSubId,
        isAdditionalSeatOrder,
        isIntervalOrder,
        // userDateFormat,
        data,
        // setData,
        stripeComplete,
        stripeErrorMessages,
        monthly,
        handleChangeFields,
        handleSaveButton,
        priceYr,
        priceMo,
        seatPrice,
        // isPromoCodeApplied,
        stripeBillingDetails,
        stripeCard,
        moreSeats,
        isUpgradeAdditionalSeat,
        isProcessingPayment,
        handleChangeStripeCardNumber,
        handleChangeStripeCardExpiry,
        handleChangeStripeCardCvc,
        // newInvoiceTotal,
        existingTier,
        isTier1,
    } = params;
    const { open: openPaymentDialog, onClose, selectedTier } = props;
    const { cardHoldersName } = data;

    // const currentDate = new Date();

    // const { type } = validPromoCodeData;
    // let monthToAdd = 1;
    // if (type === '60D') monthToAdd = 2;

    const existingCardHoldersName = stripeBillingDetails?.name || '';
    const existingCardNumber = `${stripeCard?.brand} ending in ${stripeCard?.last4}`;
    const existingCardExpiryDate = `${stripeCard?.exp_month}/${stripeCard?.exp_year}`;

    const isStripeCardPaymentReady = stripeCard || getIsStripeCardPaymentReady({ data, stripeComplete, stripeErrorMessages });

    const saveButtonLabel = isTier1 ? 'Activating' : PURCHASE;
    const saveButtonDisabled = !isTier1 && (isProcessingPayment || !isStripeCardPaymentReady);

    const defns = {
        container: {
            open: openPaymentDialog,
            onClose,
            label: isAdditionalSeatOrder
                ? stripeAdditionalSeatSubId
                    ? ADD_REMOVE_ADDITIONAL_SEATS
                    : PURCHASE_ADDITIONAL_SEATS
                : isIntervalOrder
                ? `${CHANGE_BILLING_INTERVAL} (Tier ${selectedTier?.tier})`
                : existingTier === 0 && isTier1
                ? `Activating ${getTierTitle(1)?.slice(0, -9)} (Tier 1)`
                : existingTier === 0
                ? `Purchase ${getTierTitle(selectedTier?.tier)?.slice(0, -9)} (Tier ${selectedTier?.tier})`
                : `Upgrade to ${getTierTitle(selectedTier?.tier)?.slice(0, -9)} (Tier ${selectedTier?.tier})`,
            actionButtonDefns: {
                cancel: { isHidden: isTier1 },
                save: { disabled: saveButtonDisabled, onClick: handleSaveButton, color: 'primary', label: saveButtonLabel, loading: isProcessingPayment, isHidden: isTier1 },
            },
        },
        title: { variant: 'body1', value: 'or enter card info' },
        label: { variant: 'body2' },
        cardHoldersName: { onChange: handleChangeFields, name: 'cardHoldersName', value: cardHoldersName, placeholder: PLACEHOLDER_FIELD_NAME, inputProps: { maxLength: 24 } },
        // cardNumber: {
        //     name: 'cardNumber',
        //     value: cardNumber,
        //     placeholder: '+ Ex 4242 4242 4242 4242',
        //     format: '#### #### #### ####',
        //     onValueChange: value => setData({ ...data, cardNumber: value.floatValue }),
        // },
        // cardExpiryDate: {
        //     isNumericString: true,
        //     value: cardExpiryDate,
        //     placeholder: '+ Ex MM/YY',
        //     mask: '_',
        //     // format: checkCardExpiryDate,
        //     onValueChange: value => setData({ ...data, cardExpiryDate: value.value }),
        // },
        // cardCVC: {
        //     name: 'cardCVC',
        //     value: cardCVC,
        //     placeholder: '+ Ex 1234',
        //     format: '####',
        //     onValueChange: value => setData({ ...data, cardCVC: value.floatValue }),
        // },
        stripeCardNumber: { onChange: handleChangeStripeCardNumber },
        stripeCardExpiry: { onChange: handleChangeStripeCardExpiry },
        stripeCardCvc: { onChange: handleChangeStripeCardCvc },

        stripeCardNumberErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardNumber },
        stripeCardExpiryErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardExpiry },
        stripeCardCvcErrorMessage: { variant: 'subtitle', value: stripeErrorMessages?.cardCvc },

        existingCardHoldersName: { value: existingCardHoldersName },
        existingCardNumber: { value: existingCardNumber },
        existingCardExpiryDate: { value: existingCardExpiryDate },

        cardPrice: { variant: 'body2', color: 'secondary' },
        cardPriceSpan: { variant: 'body2', component: 'span', color: 'secondary' },

        payingDetailsLineOne: {
            variant: 'caption',
            isHidden: isUpgradeAdditionalSeat,
            value: isAdditionalSeatOrder
                ? `You will be billed $${numberWithCommas(parseFloat(seatPrice * moreSeats).toFixed(2))} every month.`
                : `You will be billed ${getFormattedPrice(monthly ? priceMo : priceYr)}`,
            //     : `We will charge you $${monthly ? priceMo : priceYr} on
            // ${getFormattedDate(isPromoCodeApplied ? addMonths(currentDate, monthToAdd) : currentDate, userDateFormat)}`,
        },
        payingDetailsLineTwo: {
            variant: 'caption',
            value: isAdditionalSeatOrder ? `Cancel any time.` : `every ${monthly ? 'month' : 'year'}, Cancel any time`,
        },
        payingDetailsPayNowText: { variant: 'body1', value: 'Paying now' },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const initData = { cardHoldersName: '' };
const initStripeData = { cardNumber: null, cardExpiry: null, cardCvc: null };
const initStripeErrorMessages = { cardNumber: null, cardExpiry: null, cardCvc: null };

const getIsStripeCardPaymentReady = ({ data, stripeComplete, stripeErrorMessages }) => {
    const fieldNames = ['cardHoldersName'];
    const stripeFieldNames = ['cardNumber', 'cardExpiry', 'cardCvc'];
    const hasError = _some(stripeFieldNames, fieldName => stripeErrorMessages[fieldName]);
    const hasIncompleteStripeField = _some(stripeFieldNames, fieldName => !stripeComplete[fieldName]);
    const hasBlankField = _some(fieldNames, fieldName => !data[fieldName]);

    return !hasError && !hasIncompleteStripeField && !hasBlankField;
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function PaymentDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { userId, employerId, currentEmployer, pendingAccountSetup } = appContext;
    const userDateFormat = getUserDateFormat(appContext);
    const existingTier = _get(currentEmployer, 'tier', 0);
    const existingMonthly = _get(currentEmployer, 'billMonthly');
    const existingAdditionalSeats = _get(currentEmployer, 'additionalSeats', 0);
    const stripeCusId = currentEmployer?.stripeCusId;
    const stripeDefaultPmId = currentEmployer?.stripeDefaultPmId;
    const stripeSubId = currentEmployer?.stripeSubId;
    const stripeAdditionalSeatSubId = currentEmployer?.stripeAdditionalSeatSubId;
    const trialAvailable = currentEmployer?.trialAvailable;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    // Initialize an instance of stripe.
    const stripe = useStripe();
    const stripeElements = useElements();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { variant, open, onClose, selectedTier, monthly, setIsDirtyFlagNull, /* shouldUpdate,*/ invoices, onSuccessPurchase, currentStripeSubscriptionItemId } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [data, setData] = useState(initData);
    const [stripeComplete, setStripeComplete] = useState(initStripeData);
    const [stripeErrorMessages, setStripeErrorMessages] = useState(initStripeErrorMessages);
    const [stripeCustomer, setStripeCustomer] = useState();
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isFetchingStripeCustomer, setIsFetchingStripeCustomer] = useState(false);
    const [activateTier1SubscriptionCalled, setActivateTier1SubscriptionCalled] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isAdditionalSeatOrder = variant === 'additional-seat';
    const isIntervalOrder = variant === 'interval';
    let priceMo = parseFloat(_get(selectedTier, 'priceMo', '')).toFixed(2);
    let priceYr = parseFloat(_get(selectedTier, 'priceYr', '')).toFixed(2);
    const seatPrice = _get(selectedTier, 'seatPrice', '');
    const tier = _get(selectedTier, 'tier', 0);
    const isTier1 = tier === 1;

    const stripeBillingDetails = stripeCustomer?.invoice_settings?.default_payment_method?.billing_details;
    const stripeCard = stripeCustomer?.invoice_settings?.default_payment_method?.card || stripeCustomer?.default_source;

    const newInvoice = invoices?.newInvoice;

    // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> PaymentDialog ', { newInvoice });
    // const newInvoiceCurrentSubscriptionItemId = _get(newInvoice, 'lines.data[0].subscription_item'); // assume the first line item is the old plan
    const newInvoiceCurrentSubscriptionItemId = currentStripeSubscriptionItemId;
    const newInvoiceSubscriptionItem = _get(newInvoice, 'subscription.items.data[0]');
    const newInvoiceSubscriptionItemQuantity = newInvoiceSubscriptionItem?.quantity;
    const newInvoiceSubscriptionProrationDate = newInvoice?.subscription_proration_date; // in seconds
    // const newInvoiceSubtotal = newInvoice?.subtotal || 0;
    const newInvoiceTotal = newInvoice?.total || 0;
    const newInvoiceLineItems = _get(newInvoice, 'lines.data', []);
    const newInvoiceSubscriptionPeriodStartDt = _get(newInvoice, 'subscription.current_period_start', 0);
    const newInvoiceSubscriptionPeriodEndDt = _get(newInvoice, 'subscription.current_period_end', 0);

    // cards details
    const existingCardHoldersName = stripeBillingDetails?.name || '';
    const existingCardExpiryDate = `${stripeCard?.exp_month}/${stripeCard?.exp_year}`;
    const cardBrand = stripeCard?.brand;
    const formatCardBrand = cardBrand === 'diners' ? 'dinersclub' : cardBrand;

    let moreSeats = 0;
    let isUpgradeAdditionalSeat = null;
    let isDowngradeAdditionalSeat = null;
    if (isAdditionalSeatOrder) {
        moreSeats = _isNil(newInvoiceSubscriptionItemQuantity) ? 0 : parseInt(newInvoiceSubscriptionItemQuantity) - parseInt(existingAdditionalSeats);
        isUpgradeAdditionalSeat = moreSeats > 0;
        isDowngradeAdditionalSeat = moreSeats < 0;
    }

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {}, [])

    // load Stripe customer data
    useEffect(() => {
        const retrieveCustomer = async () => {
            setIsFetchingStripeCustomer(true);
            const retrieveCustomerResp = await retrieveStripeCustomer({ stripeCusId });
            if (retrieveCustomerResp) {
                setIsFetchingStripeCustomer(false);
                setStripeCustomer(retrieveCustomerResp.data);
            }
        };
        if (stripeCusId && open) {
            retrieveCustomer();
        }
    }, [open, stripeCusId]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeStripeCardNumber = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardNumber: complete }));

        console.log(field);

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardNumber: msg }));
    };

    const handleChangeStripeCardExpiry = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardExpiry: complete }));

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardExpiry: msg }));
    };

    const handleChangeStripeCardCvc = field => {
        const complete = !!field?.complete;
        setStripeComplete(prev => ({ ...prev, cardCvc: complete }));

        const msg = field?.error ? field.error.message : null;
        setStripeErrorMessages(prev => ({ ...prev, cardCvc: msg }));
    };

    const handleSaveButton = async () => {
        // let PromoCode = null;
        // if (!isInvalidPromoCode) PromoCode = promoCode;
        let success = true;
        setIsProcessingPayment(true);

        if (isAdditionalSeatOrder) {
            //-------------------------------------------------------------------------------------------
            // buy additional seats
            //-------------------------------------------------------------------------------------------
            let subscriptionResp = null;

            // process payment only if subscription item is found in invoice
            if (newInvoiceSubscriptionItem) {
                // if additional seat subscription already exist, update it
                if (stripeAdditionalSeatSubId) {
                    //-------------------------------------------------------------------------------------------
                    // update Stripe subscription item (assuming the customer has a default payment method)
                    //-------------------------------------------------------------------------------------------
                    subscriptionResp = await upgradeStripeAdditionalSeatSubscription({
                        stripeAdditionalSeatSubId,
                        stripeAdditionalSeatSubscriptionItemId: newInvoiceCurrentSubscriptionItemId,
                        existingQuantity: parseInt(existingAdditionalSeats),
                        quantity: newInvoiceSubscriptionItemQuantity,
                        subscriptionProrationDate: newInvoiceSubscriptionProrationDate,
                        stripeDefaultPmId,
                    });
                } else {
                    //-------------------------------------------------------------------------------------------
                    // create Stripe subscription (assuming the customer has a default payment method)
                    //-------------------------------------------------------------------------------------------
                    subscriptionResp = await createStripeAdditionalSeatSubscription({
                        stripeCusId,
                        isAdditionalSeat: true,
                        quantity: newInvoiceSubscriptionItemQuantity,
                        stripeDefaultPmId,
                        employerId,
                    });
                }

                // proceed only if stripe subscription successfully created
                if (subscriptionResp) {
                    // check the invoice status
                    const invoiceStatus = subscriptionResp.data?.invoiceStatus;

                    if (invoiceStatus === 'paid') {
                        //-------------------------------------------------------------------------------------------
                        // payment successful, activate additional seats
                        //-------------------------------------------------------------------------------------------
                        success = activateAdditionalSeats({
                            additionalSeats: newInvoiceSubscriptionItemQuantity,
                            employerId,
                            subscriptionResp,
                            isNewSubscription: !stripeAdditionalSeatSubId,
                            setSnackbarMessage,
                            setSnackbarErrorMessage,
                        });
                    } else {
                        //TODO Handle invoice payment errors
                        alert('Invoice created but not paid. Check payment method?');
                        success = false;
                    }
                } else {
                    //TODO Handle Stripe API call errors
                    alert('Stripe API call returned null. Check cloud function log.');
                    success = false;
                }
            }
        } else {
            //-------------------------------------------------------------------------------------------
            // buy tier product
            //-------------------------------------------------------------------------------------------
            // process payment only if subscription item is found in invoice
            if (newInvoiceSubscriptionItem) {
                // if tier subscription already exist, update it
                if (stripeSubId) {
                    let paymentMethodId = stripeDefaultPmId;

                    if (!stripeDefaultPmId) {
                        //-------------------------------------------------------------------------------------------
                        // Since Tier 1 is free and we did not capture payment info, the stripeDefaultPmId is null,
                        //   attempt to charge the customer's credit card.
                        //-------------------------------------------------------------------------------------------
                        // setIsUpdatingCard(true);
                        try {
                            const resp = await createCardSetupIntent({ stripeCusId });
                            // console.log(resp);
                            //-------------------------------------------------------------------------------------------
                            // NOTE: This Stripe API must be called from here for the 'card' param to work.
                            //       Pass in CardNumberElement and Stripe.js SDK will automatically pull in
                            //       CardExpiryElement and CardCvcElement.
                            //-------------------------------------------------------------------------------------------
                            if (resp && !resp?.error) {
                                const clientSecret = resp.data?.client_secret;
                                const cardHoldersName = data?.cardHoldersName;

                                const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                                    payment_method: {
                                        card: stripeElements.getElement(CardNumberElement),
                                        billing_details: getConfirmCardSetupBillingDetails({ cardHoldersName }),
                                        // billing_details: { address: { country: 'Hong Kong' } }, //! Use this to throw error
                                    },
                                });

                                //-------------------------------------------------------------------------------------------
                                // Save the card so the customer will have a default payment method before calling upgrade subscription
                                //-------------------------------------------------------------------------------------------
                                if (error) {
                                    throw new Error('Failed to confirm card setup');
                                } else {
                                    const success = await saveEmployerSetupIntentCardPaymentMethod({
                                        employerId,
                                        stripeCusId,
                                        setupIntent,
                                        setSnackbarMessage,
                                        setSnackbarErrorMessage,
                                    });
                                    if (success) {
                                        // now this payment method is the customer's default payment method
                                        paymentMethodId = setupIntent?.payment_method;

                                        //-------------------------------------------------------------------------------------------
                                        // We also want to get the subscription item id of the current product because the
                                        //   upcoming invoice resp does not have it (why? maybe because it is $0?) but we need it
                                        //   for removing the product from the subscription.
                                        //-------------------------------------------------------------------------------------------
                                    } else {
                                        throw new Error('Failed to save payment method for employer.');
                                    }
                                }
                            } else {
                                throw new Error('Failed to create Stripe setup intent');
                            }
                        } catch (error) {
                            // setIsUpdatingCard(false);
                            //TODO Handle card setup error
                            console.error(error.message);
                            alert(error.message);
                        }
                    }

                    if (paymentMethodId) {
                        //-------------------------------------------------------------------------------------------
                        // update Stripe subscription item (assuming the customer has a default payment method)
                        //-------------------------------------------------------------------------------------------
                        const subscriptionResp = await upgradeStripeSubscription({
                            stripeSubId,
                            stripeSubscriptionItemId: newInvoiceCurrentSubscriptionItemId,
                            existingTier,
                            existingMonthly,
                            tier,
                            monthly,
                            subscriptionProrationDate: newInvoiceSubscriptionProrationDate,
                            stripeDefaultPmId: paymentMethodId,
                        });
                        // proceed only if stripe subscription successfully created
                        if (subscriptionResp) {
                            //-------------------------------------------------------------------------------------------
                            // payment successful, activate tier upgrade
                            //-------------------------------------------------------------------------------------------
                            success = upgradeTierSubscription({
                                tier,
                                employerId,
                                isSelectedPlanMonthly: monthly,
                                stripeCusId,
                                subscriptionResp,
                                // paymentIntent,
                                setSnackbarMessage,
                                setSnackbarErrorMessage,
                            });
                        } else {
                            //TODO Handle Stripe API call errors
                            alert('Stripe API call returned null. Check cloud function log.');
                            success = false;
                        }
                    } else {
                        //TODO Handle it if still no payment method at this point
                    }
                } else {
                    //-------------------------------------------------------------------------------------------
                    // new subscription
                    //-------------------------------------------------------------------------------------------
                    // create stripe subscription and get client secret for payment
                    const createSubscriptionResp = await createStripeSubscription({ stripeCusId, tier, monthly, employerId, trialAvailable });

                    // proceed only if stripe subscription successfully created
                    if (createSubscriptionResp) {
                        // let setupIntentResp = null;
                        let clientSecret = null;
                        let cardHoldersName = null;
                        let intentError = null;
                        let setupIntent = null;
                        let paymentIntent = null;

                        //-------------------------------------------------------------------------------------------
                        // With the free trial, we are not going to charge the card at the time the user subscribe.
                        //-------------------------------------------------------------------------------------------
                        // if (trialAvailable) {
                        //     setupIntentResp = await createCardSetupIntent({ stripeCusId });
                        //     //-------------------------------------------------------------------------------------------
                        //     // NOTE: This Stripe API must be called from here for the 'card' param to work.
                        //     //       Pass in CardNumberElement and Stripe.js SDK will automatically pull in
                        //     //       CardExpiryElement and CardCvcElement.
                        //     //-------------------------------------------------------------------------------------------
                        //     if (setupIntentResp && !setupIntentResp?.error) {
                        //         clientSecret = setupIntentResp.data?.client_secret;
                        //         cardHoldersName = data?.cardHoldersName;

                        //         const confirmResp = await stripe.confirmCardSetup(clientSecret, {
                        //             payment_method: {
                        //                 card: stripeElements.getElement(CardNumberElement),
                        //                 billing_details: getConfirmCardSetupBillingDetails({ cardHoldersName }),
                        //                 // billing_details: { address: { country: 'Hong Kong' } }, //! Use this to throw error
                        //             },
                        //         });

                        //         intentError = confirmResp?.error;
                        //         setupIntent = confirmResp?.setupIntent;
                        //     }
                        //-------------------------------------------------------------------------------------------
                        // With Tier 1, we are not going to collect card info at the time the user subscribe.
                        //-------------------------------------------------------------------------------------------
                        if (tier !== 1) {
                            //-------------------------------------------------------------------------------------------
                            // Regular purchase. Proceed with payment.
                            //-------------------------------------------------------------------------------------------
                            //-------------------------------------------------------------------------------------------
                            // NOTE: This Stripe API must be called from here for the 'card' param to work.
                            //       Pass in CardNumberElement and Stripe.js SDK will automatically pull in
                            //       CardExpiryElement and CardCvcElement.
                            //-------------------------------------------------------------------------------------------
                            clientSecret = createSubscriptionResp.data?.cs;
                            cardHoldersName = data?.cardHoldersName;

                            const confirmResp = await stripe.confirmCardPayment(clientSecret, {
                                payment_method: {
                                    card: stripeElements.getElement(CardNumberElement),
                                    billing_details: getConfirmCardPaymentBillingDetails({ cardHoldersName }),
                                    // billing_details: { address: { country: 'Hong Kong' } }, //! Use this to throw error
                                },
                            });

                            intentError = confirmResp?.error;
                            paymentIntent = confirmResp?.paymentIntent;
                        }

                        if (intentError) {
                            //TODO Handle card payment error
                            console.error(intentError.message);
                            alert(intentError.message);
                            success = false;
                        } else {
                            //-------------------------------------------------------------------------------------------
                            // Payment successful, activate account.
                            //-------------------------------------------------------------------------------------------
                            success = activateSubscription({
                                tier,
                                userId,
                                employerId,
                                pendingAccountSetup,
                                isSelectedPlanMonthly: monthly,
                                // promoCode: PromoCode,
                                // promoCodeMessage,
                                // promoCodeType: validPromoCodeData?.type,
                                // validPromoCodeData,
                                stripeCusId,
                                createSubscriptionResp,
                                paymentIntent,
                                setupIntent,
                                setSnackbarMessage,
                                setSnackbarErrorMessage,
                            });
                        }
                    } else {
                        //TODO Handle create subscription error
                        console.error('Failed to create Stripe subscription.');
                        alert('Failed to create Stripe subscription.');
                        success = false;
                    }
                }
            }

            setIsDirtyFlagNull && setIsDirtyFlagNull();
        }

        if (success) {
            setData({ cardHoldersName: '' });
            onClose && onClose();
            onSuccessPurchase && onSuccessPurchase();
            setIsProcessingPayment(false);
        }
    };

    /**
     * Activate subscription but without payment (mainly for Tier 1).
     */
    const autoActivateSubscription = useCallback(async () => {
        if (!stripeSubId && !activateTier1SubscriptionCalled) {
            //-------------------------------------------------------------------------------------------
            // new subscription
            //-------------------------------------------------------------------------------------------
            // create stripe subscription and get client secret for payment
            const createSubscriptionResp = await createStripeSubscription({ stripeCusId, tier, monthly, employerId, trialAvailable });

            // console.log(`>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> autoActivateSubscription`, JSON.stringify({ createSubscriptionResp }));
            if (createSubscriptionResp?.data) {
                //-------------------------------------------------------------------------------------------
                // Tier 1 Stripe subscription creation successful, activate account.
                //-------------------------------------------------------------------------------------------
                const success = activateTier1Subscription({
                    userId,
                    employerId,
                    pendingAccountSetup,
                    isSelectedPlanMonthly: monthly,
                    createSubscriptionResp,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                });

                if (success) {
                    // setData({ cardHoldersName: '' });
                    onClose && onClose();
                    onSuccessPurchase && onSuccessPurchase();
                    setIsProcessingPayment(false);

                    // activateTier1Subscription should only be called once.
                    setActivateTier1SubscriptionCalled(true);
                }
            }
        }
    }, [
        stripeSubId,
        stripeCusId,
        activateTier1SubscriptionCalled,
        tier,
        monthly,
        userId,
        employerId,
        trialAvailable,
        onClose,
        onSuccessPurchase,
        pendingAccountSetup,
        setSnackbarMessage,
        setSnackbarErrorMessage,
    ]);

    //---------------------------------------------------------------------
    // Side effects (calling local functions)
    //---------------------------------------------------------------------
    useEffect(() => {
        const callAutoRunSuccessTransaction = () => {
            // console.log(`>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> useEffect callAutoRunSuccessTransaction`, JSON.stringify({ open, isFetchingStripeCustomer, stripeCustomer }));
            autoActivateSubscription();
        };

        if (open && tier === 1 && !isFetchingStripeCustomer && stripeCustomer) {
            callAutoRunSuccessTransaction();
        }
    }, [open, tier, isFetchingStripeCustomer, stripeCustomer, autoActivateSubscription]);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles()();
    const defns = getDefns({
        classes,
        props,
        stripeAdditionalSeatSubId,
        isAdditionalSeatOrder,
        isIntervalOrder,
        userDateFormat,
        data,
        setData,
        stripeComplete,
        stripeErrorMessages,
        monthly,
        handleChangeFields,
        handleSaveButton,
        priceYr,
        priceMo,
        seatPrice,
        isPromoCodeApplied: false, //TODO Fix Promo-code
        stripeBillingDetails,
        stripeCard,
        moreSeats,
        isUpgradeAdditionalSeat,
        isProcessingPayment,
        handleChangeStripeCardNumber,
        handleChangeStripeCardExpiry,
        handleChangeStripeCardCvc,
        newInvoiceTotal,
        existingTier,
        isTier1,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return isTier1 ? (
        <AfitdEditDialog {...defns.container}>
            <div {...defns.circularContainer}>
                <CircularProgress size={40} />
            </div>
        </AfitdEditDialog>
    ) : (
        <AfitdEditDialog {...defns.container}>
            {/* <AfitdTypography {...defns.title} /> */}

            {isFetchingStripeCustomer ? (
                <div {...defns.circularContainer}>
                    <CircularProgress size={40} />
                </div>
            ) : (
                <>
                    {stripeCard ? (
                        <Card
                            name={existingCardHoldersName}
                            number={`**** **** **** ${stripeCard?.last4}`}
                            expiry={existingCardExpiryDate}
                            preview={true}
                            issuer={formatCardBrand}
                        />
                    ) : (
                        // <div {...defns.existingCardInfoContainer}>
                        //     <div {...defns.inputBox} style={{ marginTop: 0 }}>
                        //         <AfitdTypography {...defns.label}>Cardholder name</AfitdTypography>
                        //         <AfitdTypography {...defns.existingCardHoldersName} />
                        //     </div>
                        //     <div>
                        //         <AfitdTypography {...defns.existingCardNumber} />
                        //         <AfitdTypography {...defns.existingCardExpiryDate} />
                        //     </div>
                        // </div>
                        <div {...defns.cardInfoContainer}>
                            <div {...defns.inputBox} style={{ marginTop: 0 }}>
                                <AfitdTypography {...defns.label}>Cardholder name</AfitdTypography>
                                <AfitdTextField {...defns.cardHoldersName} />
                            </div>
                            <div {...defns.inputBox}>
                                <AfitdTypography {...defns.label}>Card number</AfitdTypography>
                                {/* <AfitdNumberFormat {...defns.cardNumber} customInput={AfitdTextField} /> */}
                                <div {...defns.stripeCardElementDiv}>
                                    <CardNumberElement
                                        options={{
                                            showIcon: true,
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                },
                                            },
                                        }}
                                        {...defns.stripeCardNumber}
                                    />
                                </div>
                                {stripeErrorMessages?.cardNumber && <AfitdTypography {...defns.stripeCardNumberErrorMessage} />}
                            </div>
                            <div {...defns.inputBoxContainer}>
                                <div {...defns.inputBox}>
                                    <AfitdTypography {...defns.label}>Expiration Date</AfitdTypography>
                                    {/* <AfitdNumberFormat {...defns.cardExpiryDate} customInput={AfitdTextField} format={checkCardExpiryDate} /> */}
                                    <div {...defns.stripeCardElementDiv}>
                                        <CardExpiryElement
                                            options={{
                                                style: {
                                                    base: {
                                                        fontSize: '16px',
                                                    },
                                                },
                                            }}
                                            {...defns.stripeCardExpiry}
                                        />
                                    </div>
                                    {stripeErrorMessages?.cardExpiry && <AfitdTypography {...defns.stripeCardExpiryErrorMessage} />}
                                </div>
                                <div {...defns.inputBox}>
                                    <AfitdTypography {...defns.label}>CVC Code</AfitdTypography>
                                    {/* <AfitdNumberFormat {...defns.cardCVC} customInput={AfitdTextField} /> */}
                                    <div {...defns.stripeCardElementDiv}>
                                        <CardCvcElement
                                            options={{
                                                style: {
                                                    base: {
                                                        fontSize: '16px',
                                                    },
                                                },
                                            }}
                                            {...defns.stripeCardCvc}
                                        />
                                    </div>
                                    {stripeErrorMessages?.cardCvc && <AfitdTypography {...defns.stripeCardCvcErrorMessage} />}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div {...defns.invoiceContainer}>
                <br />
                {isAdditionalSeatOrder && isUpgradeAdditionalSeat && <AfitdTypography value={`You are adding ${moreSeats} more seat${moreSeats > 1 ? 's' : ''}`} />}
                {isAdditionalSeatOrder && isDowngradeAdditionalSeat && (
                    <AfitdTypography value={`You are removing ${Math.abs(moreSeats)} seat${Math.abs(moreSeats) > 1 ? 's' : ''}`} />
                )}
                <br />
                <AfitdTypography variant="h6">Upcoming Invoice Line items</AfitdTypography>
                {_map(newInvoiceLineItems, lineItem => {
                    return (
                        <div {...defns.lineItemDiv}>
                            <AfitdTypography {...defns.lineItemDescription}>
                                {' '}
                                <span {...defns.dot}>&bull;</span> {lineItem.description}
                            </AfitdTypography>
                            <AfitdTypography align="right">{getFormattedPrice(parseFloat(lineItem.amount / 100).toFixed(2))}</AfitdTypography>
                        </div>
                    );
                })}
                <div>
                    <hr />
                    <AfitdTypography align="right">
                        <b>Total = ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}</b>
                    </AfitdTypography>
                </div>

                <div>
                    <AfitdTypography
                        value={`Billing period: ${getFormattedDate(newInvoiceSubscriptionPeriodStartDt * 1000, userDateFormat)} to ${getFormattedDate(
                            newInvoiceSubscriptionPeriodEndDt * 1000,
                            userDateFormat,
                        )}`}
                    />
                </div>
            </div>
            <div {...defns.payingContainer}>
                <div {...defns.payingDetailsContainer}>
                    <AfitdTypography {...defns.payingDetailsLineOne} />
                    <AfitdTypography {...defns.payingDetailsLineTwo} />
                </div>
                <div {...defns.inline}>
                    <AfitdTypography {...defns.payingDetailsPayNowText} />
                    {isAdditionalSeatOrder ? (
                        <AfitdTypography {...defns.cardPrice}>
                            ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                            <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                        </AfitdTypography>
                    ) : monthly ? (
                        <AfitdTypography {...defns.cardPrice}>
                            ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                            <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                        </AfitdTypography>
                    ) : (
                        <AfitdTypography {...defns.cardPrice}>
                            ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                            <AfitdTypography {...defns.cardPriceSpan}>/yr</AfitdTypography>
                        </AfitdTypography>
                    )}
                </div>
            </div>
        </AfitdEditDialog>
    );
}
